import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LockKeyhole, Save } from 'lucide-react'
import { toast } from 'sonner'
import { projectApi, type CanonicalSceneSpec, type SceneLock } from '@/api/projectApi'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Project } from '@/types/nf'

type SceneLockForm = {
  scene_id: string
  setting: string
  weather: string
  lighting: string
  props: string
  override_reason: string
}

const emptyForm: SceneLockForm = {
  scene_id: '',
  setting: '',
  weather: '',
  lighting: '',
  props: '',
  override_reason: '',
}

function formFromLock(lock?: SceneLock): SceneLockForm {
  if (!lock) return emptyForm
  return {
    scene_id: lock.spec.scene_id,
    setting: lock.spec.setting?.value ?? '',
    weather: lock.spec.weather?.value ?? '',
    lighting: lock.spec.lighting?.value ?? '',
    props: lock.spec.props?.map((item) => item.value).join(', ') ?? '',
    override_reason: lock.override_reason ?? '',
  }
}

function buildSpec(values: SceneLockForm): CanonicalSceneSpec {
  return {
    scene_id: values.scene_id.trim(),
    setting: values.setting.trim() ? { value: values.setting.trim(), strictness: 'semantic', source: 'manual' } : undefined,
    weather: values.weather.trim() ? { value: values.weather.trim(), strictness: 'ordinal', source: 'manual' } : undefined,
    lighting: values.lighting.trim() ? { value: values.lighting.trim(), strictness: 'ordinal', source: 'manual' } : undefined,
    props: values.props
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => ({ value, strictness: 'exact', source: 'manual' })),
  }
}

function Field({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

export function SceneLockPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = ['project-scene-locks', project.id] as const
  const locksQuery = useQuery({
    queryKey,
    queryFn: () => projectApi.listSceneLocks(project.id),
  })
  const [selectedSceneID, setSelectedSceneID] = useState('')
  const [form, setForm] = useState<SceneLockForm>(emptyForm)
  const busy = locksQuery.isLoading

  const saveMutation = useMutation({
    mutationFn: (values: SceneLockForm) =>
      projectApi.putSceneLock(project.id, values.scene_id.trim(), {
        spec: buildSpec(values),
        override_reason: values.override_reason.trim(),
      }),
    onSuccess: (lock) => {
      queryClient.setQueryData<SceneLock[]>(queryKey, (current = []) => {
        const rest = current.filter((item) => item.scene_id !== lock.scene_id)
        return [...rest, lock].sort((a, b) => a.scene_id.localeCompare(b.scene_id))
      })
      setSelectedSceneID(lock.scene_id)
      setForm(formFromLock(lock))
      toast.success(t('sceneLock.saved'))
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('sceneLock.saveFailed')))
    },
  })

  const selectLock = (lock: SceneLock) => {
    setSelectedSceneID(lock.scene_id)
    setForm(formFromLock(lock))
  }

  const onSubmit = () => {
    if (!form.scene_id.trim()) return
    saveMutation.mutate(form)
  }

  const disabled = busy || saveMutation.isPending

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <LockKeyhole className="size-4" />
            {t('sceneLock.title')}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setForm(emptyForm)} disabled={disabled}>
            {t('sceneLock.create')}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(locksQuery.data ?? []).map((lock) => (
            <button key={lock.scene_id} type="button" onClick={() => selectLock(lock)}>
              <Badge variant={lock.scene_id === selectedSceneID ? 'default' : 'secondary'}>{lock.scene_id}</Badge>
            </button>
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-2" onKeyDown={(event) => {
          if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
            event.preventDefault()
            event.stopPropagation()
            if (!disabled) onSubmit()
          }
        }}>
          <Field id="scene-lock-id" label={t("sceneLock.id")} value={form.scene_id} disabled={disabled} onChange={(scene_id) => setForm((prev) => ({ ...prev, scene_id }))} />
          <Field id="scene-lock-setting" label={t("sceneLock.setting")} value={form.setting} disabled={disabled} onChange={(setting) => setForm((prev) => ({ ...prev, setting }))} />
          <Field id="scene-lock-weather" label={t("sceneLock.weather")} value={form.weather} disabled={disabled} onChange={(weather) => setForm((prev) => ({ ...prev, weather }))} />
          <Field id="scene-lock-lighting" label={t("sceneLock.lighting")} value={form.lighting} disabled={disabled} onChange={(lighting) => setForm((prev) => ({ ...prev, lighting }))} />
          <Field id="scene-lock-props" label={t("sceneLock.props")} value={form.props} disabled={disabled} onChange={(props) => setForm((prev) => ({ ...prev, props }))} />
          <Field id="scene-lock-reason" label={t("sceneLock.reason")} value={form.override_reason} disabled={disabled} onChange={(override_reason) => setForm((prev) => ({ ...prev, override_reason }))} />
          <div className="md:col-span-2">
            <Button type="button" onClick={onSubmit} disabled={disabled || !form.scene_id.trim()} className="gap-2">
              <Save className="size-4" />
              {t('sceneLock.save')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
